import { useCallback, useEffect, useRef, useState } from 'react';

import { Nullable } from '~types/Nullable';

// Waits for scrolling to stop, so a fast scroll does not react to every row it passes.
export const ON_SCREEN_SETTLE_IN_MS = 300;

const haveSameIds = (first: ReadonlySet<string>, second: ReadonlySet<string>) =>
  first.size === second.size && [...first].every((id) => second.has(id));

export default function useOnScreenRowIds() {
  const [onScreenRowIds, setOnScreenRowIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const visibleRowIdsRef = useRef(new Set<string>());
  const nodesByRowIdRef = useRef(new Map<string, Element>());
  const rowIdsByNodeRef = useRef(new Map<Element, string>());
  const observerRef = useRef<Nullable<IntersectionObserver>>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const scheduleCommit = useCallback(() => {
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      const visibleRowIds = new Set(visibleRowIdsRef.current);
      setOnScreenRowIds((current) =>
        haveSameIds(current, visibleRowIds) ? current : visibleRowIds,
      );
    }, ON_SCREEN_SETTLE_IN_MS);
  }, []);

  useEffect(() => {
    const visibleRowIds = visibleRowIdsRef.current;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const rowId = rowIdsByNodeRef.current.get(entry.target);
        if (!rowId) return;

        if (entry.isIntersecting) {
          visibleRowIds.add(rowId);
        } else {
          visibleRowIds.delete(rowId);
        }
      });
      scheduleCommit();
    });
    observerRef.current = observer;
    nodesByRowIdRef.current.forEach((node) => observer.observe(node));

    return () => {
      observer.disconnect();
      observerRef.current = null;
      visibleRowIds.clear();
      clearTimeout(settleTimerRef.current);
    };
  }, [scheduleCommit]);

  const observeRow = useCallback(
    (rowId: string, node: Nullable<Element>) => {
      const previousNode = nodesByRowIdRef.current.get(rowId);
      if (previousNode === node) return;

      if (previousNode) {
        observerRef.current?.unobserve(previousNode);
        rowIdsByNodeRef.current.delete(previousNode);
        nodesByRowIdRef.current.delete(rowId);
        if (visibleRowIdsRef.current.delete(rowId)) scheduleCommit();
      }

      if (node) {
        nodesByRowIdRef.current.set(rowId, node);
        rowIdsByNodeRef.current.set(node, rowId);
        observerRef.current?.observe(node);
      }
    },
    [scheduleCommit],
  );

  return { observeRow, onScreenRowIds };
}
